import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Alert,
  Linking
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import CampayService from './CampayService';
import PurchaseService from './PurchaseService';
import { addDoc, collection, serverTimestamp, doc, updateDoc } from 'firebase/firestore';
import { db } from '../private/firebaseConfig';

// Temporisation entre les vérifications de statut
const STATUS_CHECK_INITIAL_DELAY = 5000; // 5 secondes avant la première vérification
const STATUS_CHECK_INTERVAL = 15000; // 15 secondes entre les vérifications suivantes
const MAX_CHECK_COUNT = 20; // Nombre maximum de vérifications automatiques

const CampayPaymentForm = ({
  amount,
  document,
  documentDetail,
  userId,
  onSuccess,
  onError,
  theme,
  paymentMethod,  // 'mtn' ou 'orange'
  navigation
}) => {
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [reference, setReference] = useState(null);
  const [checking, setChecking] = useState(false);
  const [checkCount, setCheckCount] = useState(0);
  const [showUssdCode, setShowUssdCode] = useState(false);
  const [ussdCode, setUssdCode] = useState('');
  const [operator, setOperator] = useState('');
  const [paymentLink, setPaymentLink] = useState(null);
  const [pendingPaymentId, setPendingPaymentId] = useState(null);

  // Valider le numéro de téléphone (simple validation de longueur)
  const validatePhone = () => {
    const cleanPhone = phone.replace(/\s+/g, '');
    
    if (!cleanPhone || cleanPhone.length < 9) {
      Alert.alert('Erreur', 'Veuillez entrer un numéro de téléphone valide');
      return false;
    }
    
    return true;
  };

  // Créer une notification
  const createNotification = async (documentId, documentDetailTitle, documentType, documentTitle, groupId = null) => {
    if (!userId) return;
    
    try {
      const notificationData = {
        userId: userId,
        title: 'Achat réussi !',
        message: `Votre achat de "${documentTitle}" a été effectué avec succès.`,
        type: 'PAYMENT',
        read: false,
        data: {
          documentId,
          documentType,
          ...(documentDetailTitle ? { documentDetailId: documentDetailTitle } : {}),
          ...(groupId ? { groupId } : {}),
          navigateTo: 'DocumentDetailsScreen',
          params: { document }
        },
        createdAt: serverTimestamp()
      };

      await addDoc(collection(db, 'notifications'), notificationData);
    } catch (error) {
      console.error('Error creating notification:', error);
    }
  };

  // Enregistrer l'achat dans Firestore
  const savePurchaseRecord = async (transactionReference) => {
    try {
      if (!userId) throw new Error('Utilisateur non connecté');

      // Si un pending payment existe, utiliser PurchaseService.confirmPendingPayment
      if (pendingPaymentId) {
        console.log('Confirmation du pending payment:', pendingPaymentId);
        const result = await PurchaseService.confirmPendingPayment(pendingPaymentId, transactionReference);

        // Créer la notification
        await createNotification(
          document.id,
          document.type === 'concours' ? documentDetail.titre : null,
          document.type,
          document.type === 'concours' ? documentDetail.titre : document.titre,
          documentDetail?.groupId
        );

        return result;
      }

      // Fallback : enregistrement direct si pas de pending (cas legacy / compatibilité)
      let purchaseData = {
        userId: userId,
        documentId: document.id,
        documentType: document.type,
        paymentMethod: paymentMethod,
        paymentReference: transactionReference,
        status: 'completed',
        purchaseDate: serverTimestamp(),
      };

      // Ajouter les données spécifiques selon le type de document
      if (document.type === 'concours') {
        purchaseData = {
          ...purchaseData,
          titre: documentDetail.titre,
          annee: documentDetail.annee,
          filiere: documentDetail.filiere,
          nombrePages: documentDetail.nombrePages,
          prix: documentDetail.prixFinal || documentDetail.prix,
          prixInitial: documentDetail.prixInitial,
          prixFinal: documentDetail.prixFinal,
          reductionPourcent: documentDetail.reductionPourcent,
          gratuit: documentDetail.gratuit,
          ...(documentDetail.groupId ? { groupId: documentDetail.groupId } : {})
        };
      } else if (document.type === 'bords') {
        purchaseData = {
          ...purchaseData,
          titre: document.titre,
          prix: document.prixFinal || document.prix,
          prixInitial: document.prixInitial,
          prixFinal: document.prixFinal,
          reductionPourcent: document.reductionPourcent,
          nombrePages: document.nombrePages,
        };
      }

      // Vérifier les doublons avant d'enregistrer
      const existing = await PurchaseService.checkExistingPurchase(
        userId,
        document.id,
        document.type,
        purchaseData
      );

      if (existing) {
        console.log('Achat déjà existant, pas de doublon');
        return existing;
      }

      // Enregistrer dans Firestore
      await addDoc(collection(db, 'purchases'), purchaseData);

      // Créer la notification
      await createNotification(
        document.id,
        document.type === 'concours' ? documentDetail.titre : null,
        document.type,
        document.type === 'concours' ? documentDetail.titre : document.titre,
        documentDetail?.groupId
      );

      return purchaseData;
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement de l\'achat:', error);
      throw error;
    }
  };

  // Initier le paiement avec les améliorations
  const initiatePayment = async () => {
    if (!validatePhone()) return;
    
    setLoading(true);
    setStatus('pending');
    
    try {
      // Générer une référence enrichie
      const orderRef = CampayService.generateEnrichedReference(document, documentDetail, userId);
      
      // Créer des métadonnées enrichies
      const metadata = CampayService.createTransactionMetadata(document, documentDetail, userId);
      
      // Générer une description enrichie
      const enrichedDescription = CampayService.generateEnrichedDescription(document, documentDetail);
      
      console.log('Initiation du paiement Campay pour:', {
        amount,
        phone,
        reference: orderRef,
        description: enrichedDescription,
        metadata
      });
      
      // Logger la transaction
      CampayService.logTransaction('INITIATE_PAYMENT', {
        amount,
        phone: phone.substring(0, 6) + '***', // Masquer partiellement le numéro
        reference: orderRef,
        description: enrichedDescription,
        documentType: document.type,
        userId: userId?.substring(0, 8) + '***'
      });
      
      // Créer un pending payment AVANT d'appeler CamPay
      const pendingMetadata = {};
      if (document.type === 'concours' && documentDetail) {
        pendingMetadata.titre = documentDetail.titre || '';
        pendingMetadata.annee = documentDetail.annee || '';
        pendingMetadata.filiere = documentDetail.filiere || '';
        pendingMetadata.nombrePages = documentDetail.nombrePages || '';
        pendingMetadata.prix = documentDetail.prix || amount;
        pendingMetadata.gratuit = documentDetail.gratuit || false;
        if (documentDetail.groupId) pendingMetadata.groupId = documentDetail.groupId;
      } else if (document.type === 'bords') {
        pendingMetadata.titre = document.titre || '';
        pendingMetadata.prix = document.prix || amount;
        pendingMetadata.nombrePages = document.nombrePages || '';
      }

      const pendingId = await PurchaseService.createPendingPayment({
        userId,
        documentId: document.id,
        documentType: document.type === 'simple' ? 'documents' : document.type,
        paymentMethod: operator || 'mobile_money',
        paymentReference: orderRef,
        amount: parseFloat(amount),
        metadata: pendingMetadata,
      });
      setPendingPaymentId(pendingId);

      const result = await CampayService.initiatePayment(
        amount,
        phone,
        enrichedDescription,
        orderRef,
        metadata
      );

      console.log('Réponse initiation paiement:', result);

      if (result && result.reference) {
        setReference(result.reference);
        setShowUssdCode(true);

        // CRITIQUE: enregistrer la référence CamPay dans le pending payment.
        // Sans elle, la réconciliation au démarrage ne peut pas re-vérifier
        // le paiement auprès de CamPay (référence interne ORDER-... inconnue de CamPay).
        if (pendingId) {
          try {
            await updateDoc(doc(db, 'pending_payments', pendingId), {
              campayReference: result.reference,
            });
          } catch (e) {
            console.log('Could not save campay reference to pending:', e.message);
          }
        }

        // Stocker le code USSD et l'opérateur s'ils sont fournis
        if (result.ussd_code) {
          setUssdCode(result.ussd_code);
        }
        if (result.operator) {
          setOperator(result.operator);
        }
        
        // Logger le succès de l'initiation
        CampayService.logTransaction('PAYMENT_INITIATED', { reference: result.reference }, result);
        
        // Commencer à vérifier le statut du paiement après un délai initial
        setTimeout(() => checkPaymentStatus(result.reference), STATUS_CHECK_INITIAL_DELAY);
      } else {
        throw new Error('Référence de transaction non reçue');
      }
    } catch (error) {
      console.error('Erreur d\'initiation de paiement:', error);
      setStatus('error');
      
      // Logger l'erreur
      CampayService.logTransaction('PAYMENT_INITIATION_FAILED', {
        amount,
        phone: phone.substring(0, 6) + '***',
        documentType: document.type
      }, null, error);
      
      // Si l'initiation directe échoue, essayer de générer un lien de paiement
      try {
        const orderRef = CampayService.generateEnrichedReference(document, documentDetail, userId);
        const enrichedDescription = CampayService.generateEnrichedDescription(document, documentDetail);
        const metadata = CampayService.createTransactionMetadata(document, documentDetail, userId);
        
        console.log('Génération d\'un lien de paiement alternatif...');
        
        const linkResult = await CampayService.generatePaymentLink(
          amount,
          enrichedDescription,
          orderRef,
          '', // firstName
          '', // lastName
          '', // email
          metadata
        );
        
        if (linkResult && linkResult.link) {
          setPaymentLink(linkResult.link);
          Alert.alert(
            'Paiement par lien',
            'Nous avons généré un lien de paiement alternatif. Voulez-vous l\'ouvrir?',
            [
              {
                text: 'Annuler',
                style: 'cancel'
              },
              {
                text: 'Ouvrir',
                onPress: () => Linking.openURL(linkResult.link)
              }
            ]
          );
        }
      } catch (linkError) {
        console.error('Erreur de génération de lien:', linkError);
        CampayService.logTransaction('PAYMENT_LINK_FAILED', {}, null, linkError);
      }
      
      onError && onError(error);
      Alert.alert(
        'Erreur',
        'Une erreur est survenue lors de l\'initiation du paiement. Veuillez réessayer.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Vérifier le statut du paiement
  const checkPaymentStatus = async (ref) => {
    if (!ref) return;
    
    setChecking(true);
    
    try {
      const statusResult = await CampayService.checkPaymentStatus(ref);
      console.log('Statut du paiement:', statusResult);
      
      if (statusResult && statusResult.status) {
        setStatus(statusResult.status);
        
        // Logger la vérification de statut
        CampayService.logTransaction('STATUS_CHECK', { reference: ref }, statusResult);
        
        // Paiement réussi
        if (CampayService.isPaymentComplete(statusResult.status)) {
          console.log('Paiement confirmé avec succès!');
          
          // Logger le succès
          CampayService.logTransaction('PAYMENT_SUCCESS', { reference: ref }, statusResult);
          
          try {
            // Sauvegarder les détails de l'achat
            const purchaseData = await savePurchaseRecord(ref);
            
            // Afficher le message de succès avec redirection
            Alert.alert(
              '✅ Achat réussi !',
              `Votre paiement a été confirmé avec succès.\n\nLe document "${document.type === 'concours' ? documentDetail.titre : document.titre}" est maintenant disponible dans la section "Mes Documents".`,
              [
                {
                  text: 'Voir mes documents',
                  onPress: () => {
                    // Appeler onSuccess d'abord
                    onSuccess && onSuccess(purchaseData);
                    
                    // Rediriger vers l'onglet Documents de TabNavigatorUser
                    if (navigation) {
                      // Si on est déjà dans le TabNavigator, naviguer vers l'onglet Documents
                      // navigation.navigate('Documents');
                      
                      // Ou si on doit d'abord aller au TabNavigator puis à l'onglet Documents
                      navigation.navigate('TabNavigatorUser', { screen: 'Documents' });
                    }
                  }
                },
                {
                  text: 'OK',
                  onPress: () => {
                    onSuccess && onSuccess(purchaseData);
                  }
                }
              ]
            );
          } catch (error) {
            console.error('Erreur lors de l\'enregistrement de l\'achat:', error);
            Alert.alert(
              'Attention',
              'Paiement réussi mais une erreur est survenue lors de l\'enregistrement. Veuillez contacter le support.'
            );
          }
          return;
        }
        
        // Paiement échoué
        if (CampayService.isPaymentFailed(statusResult.status)) {
          setStatus('failed');
          
          // Logger l'échec
          CampayService.logTransaction('PAYMENT_FAILED', { reference: ref }, statusResult);
          
          Alert.alert(
            'Paiement échoué',
            'Le paiement n\'a pas pu être complété. Veuillez réessayer.'
          );
          return;
        }
        
        // Paiement toujours en attente, continuer à vérifier
        if (checkCount < MAX_CHECK_COUNT) {
          setCheckCount(prevCount => prevCount + 1);
          setTimeout(() => checkPaymentStatus(ref), STATUS_CHECK_INTERVAL);
        } else {
          // Trop de vérifications, proposer le lien de paiement comme alternative
          Alert.alert(
            'Paiement en attente',
            'Le paiement est toujours en attente. Que souhaitez-vous faire?',
            [
              {
                text: 'Annuler',
                style: 'cancel'
              },
              {
                text: 'Vérifier à nouveau',
                onPress: () => {
                  setCheckCount(0);
                  setTimeout(() => checkPaymentStatus(ref), STATUS_CHECK_INITIAL_DELAY);
                }
              },
              {
                text: 'Essayer avec un lien',
                onPress: async () => {
                  try {
                    setLoading(true);
                    const orderRef = CampayService.generateEnrichedReference(document, documentDetail, userId);
                    const enrichedDescription = CampayService.generateEnrichedDescription(document, documentDetail);
                    const metadata = CampayService.createTransactionMetadata(document, documentDetail, userId);
                    
                    const linkResult = await CampayService.generatePaymentLink(
                      amount,
                      enrichedDescription,
                      orderRef,
                      '', // firstName
                      '', // lastName
                      '', // email
                      metadata
                    );
                    
                    if (linkResult && linkResult.link) {
                      setPaymentLink(linkResult.link);
                      Linking.openURL(linkResult.link);
                    }
                  } catch (error) {
                    console.error('Erreur de génération de lien:', error);
                    Alert.alert('Erreur', 'Impossible de générer un lien de paiement');
                  } finally {
                    setLoading(false);
                  }
                }
              }
            ]
          );
        }
      }
    } catch (error) {
      console.error('Erreur lors de la vérification du statut:', error);
      
      // Logger l'erreur de vérification
      CampayService.logTransaction('STATUS_CHECK_ERROR', { reference: ref }, null, error);
      
      if (checkCount < 10) {
        setCheckCount(prevCount => prevCount + 1);
        setTimeout(() => checkPaymentStatus(ref), STATUS_CHECK_INTERVAL);
      } else {
        Alert.alert(
          'Erreur',
          'Impossible de vérifier le statut du paiement. Veuillez vérifier votre transaction dans votre application mobile.'
        );
      }
    } finally {
      setChecking(false);
    }
  };

  // Gérer la vérification manuelle de statut
  const handleManualStatusCheck = () => {
    if (reference) {
      setCheckCount(0); // Réinitialiser le compteur
      checkPaymentStatus(reference);
    }
  };

  // Instructions USSD pour l'utilisateur
  const renderUssdInstructions = () => {
    const operatorName = operator?.toLowerCase() || paymentMethod;
    const isOrange = operatorName.includes('orange');
    const isMtn = operatorName.includes('mtn');
    
    return (
      <View style={styles.ussdInstructions}>
        <Text style={[styles.ussdTitle, { color: theme.primary }]}>
          Instructions de paiement
        </Text>
        
        <View style={styles.instructionContainer}>
          <MaterialCommunityIcons 
            name="cellphone" 
            size={24} 
            color={isOrange ? '#FF6600' : (isMtn ? '#FFCA28' : theme.secondary)} 
          />
          <View style={styles.instruction}>
            <Text style={[styles.instructionText, { color: theme.text }]}>
              1. Vérifiez les notifications ou SMS sur votre téléphone pour valider la transaction
            </Text>
          </View>
        </View>
        
        <View style={styles.instructionContainer}>
          <MaterialCommunityIcons 
            name="check-circle" 
            size={24} 
            color={theme.primary} 
          />
          <View style={styles.instruction}>
            <Text style={[styles.instructionText, { color: theme.text }]}>
              2. Si vous ne recevez pas de notification, tapez le code {isOrange ? '#150*50#' : '*126*2#'} et vérifiez les transactions en attente
            </Text>
          </View>
        </View>
        
        <View style={styles.instructionContainer}>
          <MaterialCommunityIcons 
            name="key" 
            size={24} 
            color={theme.secondary || theme.primary} 
          />
          <View style={styles.instruction}>
            <Text style={[styles.instructionText, { color: theme.text }]}>
              3. Confirmez le paiement avec votre code PIN {isOrange ? 'Orange Money' : 'MTN MoMo'}
            </Text>
          </View>
        </View>
        
        {checking ? (
          <View style={styles.checkingStatus}>
            <ActivityIndicator color={theme.primary} size="small" />
            <Text style={[styles.checkingText, { color: theme.secondary || theme.primary }]}>
              Vérification du paiement...
            </Text>
          </View>
        ) : (
          <TouchableOpacity
            style={[styles.checkButton, { backgroundColor: theme.primary }]}
            onPress={handleManualStatusCheck}
          >
            <Text style={styles.checkButtonText}>Vérifier le statut</Text>
          </TouchableOpacity>
        )}
        
        {paymentLink && (
          <TouchableOpacity
            style={[styles.linkButton, { backgroundColor: theme.secondary || theme.primaryDark }]}
            onPress={() => Linking.openURL(paymentLink)}
          >
            <Text style={styles.buttonText}>Utiliser le lien de paiement</Text>
          </TouchableOpacity>
        )}
      </View>
    );
  };

  // Statut du paiement avec message de succès amélioré
  const renderStatus = () => {
    if (!status || status === 'pending') return null;
    
    let statusColor = theme.secondary || theme.primary;
    let statusIcon = 'clock';
    let statusText = 'En attente de confirmation';
    
    if (CampayService.isPaymentComplete(status)) {
      statusColor = 'green';
      statusIcon = 'check-circle';
      statusText = 'Paiement confirmé !';
    } else if (CampayService.isPaymentFailed(status) || status === 'failed' || status === 'error') {
      statusColor = theme.danger || 'red';
      statusIcon = 'times-circle';
      statusText = 'Paiement échoué';
    }
    
    return (
      <View style={[styles.statusContainer, { borderColor: statusColor }]}>
        <MaterialCommunityIcons name={statusIcon} size={24} color={statusColor} />
        <Text style={[styles.statusText, { color: statusColor }]}>
          {statusText}
        </Text>
        
        {CampayService.isPaymentComplete(status) && (
          <View style={styles.successInfo}>
            <MaterialCommunityIcons name="file-check" size={20} color={statusColor} />
            <Text style={[styles.successText, { color: theme.text }]}>
              Document disponible dans "Mes Documents"
            </Text>
          </View>
        )}
        
        {reference && (
          <Text style={[styles.referenceText, { color: theme.secondary || theme.primary }]}>
            Référence: {reference}
          </Text>
        )}
      </View>
    );
  };

  return (
    <View style={[styles.container, { backgroundColor: theme.surface }]}>
      <Text style={[styles.formTitle, { color: theme.text }]}>
        Paiement via {paymentMethod === 'mtn' ? 'Mobile Money' : 'Orange Money'}
      </Text>
      
      {!showUssdCode ? (
        <>
          <View style={styles.inputContainer}>
            <Text style={[styles.label, { color: theme.text }]}>
              Numéro de téléphone Mobile Money
            </Text>
            <TextInput
              style={[
                styles.input,
                { 
                  borderColor: theme.primary,
                  backgroundColor: theme.background,
                  color: theme.text
                }
              ]}
              placeholder="Exemple: 6XXXXXXXX"
              placeholderTextColor={theme.textSecondary}
              keyboardType="phone-pad"
              value={phone}
              onChangeText={setPhone}
              editable={!loading}
            />
            <Text style={[styles.infoText, { color: theme.textSecondary }]}>
              Entrez le numéro associé à votre compte {paymentMethod === 'mtn' ? 'MTN MoMo' : 'Orange Money'}
            </Text>
          </View>
          
          <TouchableOpacity
            style={[
              styles.payButton,
              { backgroundColor: theme.primary },
              loading && styles.disabledButton
            ]}
            onPress={initiatePayment}
            disabled={loading}
          >
            {loading ? (
              <ActivityIndicator color="#FFFFFF" />
            ) : (
              <Text style={styles.payButtonText}>
                Payer {amount} XAF
              </Text>
            )}
          </TouchableOpacity>
        </>
      ) : (
        <>
          {renderUssdInstructions()}
          {renderStatus()}
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 20,
    borderRadius: 16,
    margin: 10,
  },
  formTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 20,
  },
  inputContainer: {
    marginBottom: 20,
  },
  label: {
    fontSize: 16,
    marginBottom: 8,
  },
  input: {
    height: 50,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 15,
    fontSize: 16,
  },
  infoText: {
    fontSize: 12,
    marginTop: 5,
    fontStyle: 'italic',
  },
  payButton: {
    height: 50,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 10,
  },
  disabledButton: {
    opacity: 0.6,
  },
  payButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: 'bold',
  },
  ussdInstructions: {
    marginTop: 10,
  },
  ussdTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 15,
  },
  instructionContainer: {
    flexDirection: 'row',
    marginBottom: 15,
    alignItems: 'flex-start',
  },
  instruction: {
    marginLeft: 15,
    flex: 1,
  },
  instructionText: {
    fontSize: 16,
    lineHeight: 24,
  },
  statusContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderRadius: 8,
    padding: 15,
    marginTop: 20,
  },
  statusText: {
    fontSize: 18,
    fontWeight: 'bold',
    marginTop: 10,
    marginBottom: 5,
  },
  referenceText: {
    fontSize: 14,
    marginTop: 5,
  },
  successInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 10,
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: 'rgba(34, 197, 94, 0.1)',
    borderRadius: 8,
  },
  successText: {
    fontSize: 14,
    marginLeft: 8,
    fontWeight: '500',
  },
  checkingStatus: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 15,
  },
  checkingText: {
    marginLeft: 10,
    fontSize: 14,
  },
  checkButton: {
    borderRadius: 8,
    padding: 12,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 15,
  },
  linkButton: {
    borderRadius: 8,
    padding: 12,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 10,
  },
  checkButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: 'bold',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: 'bold',
  }
});

export default CampayPaymentForm;